// src/app/pages/app/AccountPage.jsx
import React, { useMemo, useState } from "react";
import { Shield, Copy, KeyRound, Smartphone, BadgeCheck, LogOut } from "lucide-react";
import { useAuthSession } from "../../features/auth/useAuth.js";

function copyToClipboard(text) {
  if (!text) return;
  navigator.clipboard?.writeText(text).catch(() => {});
}

function Field({ label, value, onCopy }) {
  return (
    <div className="rounded-xl border border-white/10 bg-slate-900 p-4">
      <div className="text-xs text-slate-400">{label}</div>
      <div className="mt-1 flex items-center justify-between gap-3">
        <div className="text-sm text-white break-all">{value || "—"}</div>
        {onCopy && (
          <button
            type="button"
            onClick={onCopy}
            className="shrink-0 inline-flex items-center gap-2 rounded-lg bg-slate-800 hover:bg-slate-700 px-3 py-2 text-xs text-slate-200"
          >
            <Copy size={14} />
            Copy
          </button>
        )}
      </div>
    </div>
  );
}

export default function AccountPage() {
  const { session, user, logout } = useAuthSession();

  // Backend will later return user object with account_id, email, role, kyc_status, created_at, etc.
  // For now, we support both shapes:
  const profile = useMemo(() => {
    const u = user || session?.user || {};
    return {
      accountId: u.account_id || u.accountId || u.accountID || "",
      email: u.email || "",
      username: u.username || "",
      firstName: u.first_name || u.firstName || "",
      lastName: u.last_name || u.lastName || "",
      role: u.role || "USER",
      kyc: u.kyc_status || u.kycStatus || "UNVERIFIED",
      createdAt: u.created_at || u.createdAt || "",
      twoFA: u.two_fa_enabled || u.twoFAEnabled || false,
    };
  }, [user, session]);

  const [twoFAEnabled, setTwoFAEnabled] = useState(!!profile.twoFA);
  const [pwModalOpen, setPwModalOpen] = useState(false);

  const displayName = [profile.firstName, profile.lastName].filter(Boolean).join(" ") || profile.username || "Account";

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Account</h1>
          <p className="text-sm text-slate-400 mt-1">
            Profile details and security settings.
          </p>
        </div>

        <button
          type="button"
          onClick={logout}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-800 hover:bg-slate-700 px-4 py-2 text-sm text-slate-200"
        >
          <LogOut size={16} />
          Sign out
        </button>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Profile */}
        <div className="col-span-12 lg:col-span-7 space-y-4">
          <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900 to-slate-950 p-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-indigo-600/30 flex items-center justify-center">
                <BadgeCheck className="text-indigo-300" size={20} />
              </div>
              <div>
                <div className="text-white font-semibold">{displayName}</div>
                <div className="text-xs text-slate-400">
                  Role: <span className="text-slate-200">{profile.role}</span> • KYC:{" "}
                  <span className={profile.kyc === "VERIFIED" ? "text-emerald-300" : "text-amber-300"}>
                    {profile.kyc}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <Field
            label="Account ID (use this to login)"
            value={profile.accountId}
            onCopy={() => copyToClipboard(profile.accountId)}
          />
          <Field
            label="Email"
            value={profile.email}
            onCopy={() => copyToClipboard(profile.email)}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Username" value={profile.username} />
            <Field label="Created" value={profile.createdAt} />
          </div>
        </div>

        {/* Security */}
        <div className="col-span-12 lg:col-span-5 space-y-4">
          <div className="rounded-2xl border border-white/10 bg-slate-900 p-5">
            <div className="flex items-center gap-2">
              <Shield className="text-emerald-300" size={18} />
              <h2 className="text-white font-semibold">Security</h2>
            </div>

            <div className="mt-4 space-y-3">
              <div className="rounded-xl border border-white/10 bg-slate-950 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Smartphone className="text-slate-300" size={18} />
                  <div>
                    <div className="text-sm text-white">Two-factor authentication</div>
                    <div className="text-xs text-slate-400">Protect your account with 2FA.</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setTwoFAEnabled((v) => !v)}
                  className={`rounded-lg px-3 py-2 text-xs font-semibold ${
                    twoFAEnabled ? "bg-emerald-600/20 text-emerald-200" : "bg-slate-800 text-slate-200"
                  }`}
                  title="UI only for now (wire to backend later)"
                >
                  {twoFAEnabled ? "Enabled" : "Disabled"}
                </button>
              </div>

              <div className="rounded-xl border border-white/10 bg-slate-950 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <KeyRound className="text-slate-300" size={18} />
                  <div>
                    <div className="text-sm text-white">Password</div>
                    <div className="text-xs text-slate-400">Change your password regularly.</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setPwModalOpen(true)}
                  className="rounded-lg bg-indigo-600 hover:bg-indigo-500 px-3 py-2 text-xs font-semibold text-white"
                >
                  Change
                </button>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-slate-900 p-5">
            <div className="text-white font-semibold">Active sessions</div>
            <div className="mt-3 space-y-2 text-sm">
              <div className="rounded-lg border border-white/10 bg-slate-950 p-3">
                <div className="text-slate-200">This device</div>
                <div className="text-xs text-slate-400">Last active: just now</div>
              </div>
              <div className="rounded-lg border border-white/10 bg-slate-950 p-3">
                <div className="text-slate-200">Other device</div>
                <div className="text-xs text-slate-400">Last active: 2 days ago</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Change Password Modal (UI placeholder) */}
      {pwModalOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-2xl bg-slate-900 border border-white/10 p-5">
            <div className="text-white font-semibold">Change password</div>
            <p className="text-sm text-slate-400 mt-1">
              UI ready. Next step is to connect to backend endpoint.
            </p>

            <div className="mt-4 space-y-3">
              <input className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-white" placeholder="Current password" type="password" />
              <input className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-white" placeholder="New password" type="password" />
              <input className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-white" placeholder="Confirm new password" type="password" />
            </div>

            <div className="mt-5 flex gap-2 justify-end">
              <button
                className="rounded-lg bg-slate-800 hover:bg-slate-700 px-4 py-2 text-sm text-slate-200"
                onClick={() => setPwModalOpen(false)}
              >
                Close
              </button>
              <button
                className="rounded-lg bg-indigo-600 hover:bg-indigo-500 px-4 py-2 text-sm text-white opacity-70 cursor-not-allowed"
                disabled
                title="Wire to backend next"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
